import { getCatalogRamSnapshotCache } from './catalogRamSnapshotCache';
import { requireSessionOrganizationId } from '../../shell/session/sessionOrganization';
import { logBusinessAuditEvent } from '../../shell/audit/auditLogs';
import { captureOpsFailure } from '../../shared/sentry/sentryOps';
import { submitCommand } from '../../sync/core/submitCommand';
import { BrandNameDuplicateError } from '../domain/catalogErrors';
import {
  normalizeBrandName,
  toBrandSyncPayload,
  type BrandRecord,
} from './brandsRepoShared';
import type {
  CreateBrandParams,
  UpdateBrandParams,
} from '../domain/catalogBrandTypes';

const findExistingBrandIdByName = async (
  orgId: string,
  name: string,
  excludeBrandId?: string,
): Promise<string | null> => {
  const cached = getCatalogRamSnapshotCache()?.brands ?? [];
        const normalized = name.trim().toLowerCase();
        const match = cached.find(
          (b) =>
            String(b.organization_id ?? '') === orgId &&
            !b.deleted_at &&
            String(b.id) !== (excludeBrandId ?? '') &&
            String(b.name ?? '').trim().toLowerCase() === normalized,
        );
        return match?.id != null ? String(match.id) : null;
};

/**
 * @param opts.reuseExisting — solo para imports masivos (CSV): devuelve el id
 * existente en vez de lanzar BrandNameDuplicateError. La UI siempre quiere el
 * error inline, no un "éxito" silencioso.
 */
export const createBrand = async (
  params: CreateBrandParams,
  opts?: { reuseExisting?: boolean },
): Promise<string> => {
  const orgId = await requireSessionOrganizationId();
  const name = normalizeBrandName(params.name);
  if (!name) {
    throw new Error('Ingresá un nombre de marca.');
  }
  const existing = await findExistingBrandIdByName(orgId, name);
  if (existing) {
    if (opts?.reuseExisting) return existing;
    throw new BrandNameDuplicateError();
  }
  const id = crypto.randomUUID();
  const ts = new Date().toISOString();
  const brand: BrandRecord = {
    id,
    organizationId: orgId,
    name,
    code: params.code?.trim() || null,
    website: params.website?.trim() || null,
    defaultMarkupPercent: params.defaultMarkupPercent ?? null,
    isActive: params.isActive ?? true,
    deletedAt: null,
    createdAt: ts,
    updatedAt: ts,
  };

  const result = await submitCommand({
          entity: 'brand',
          operation: 'insert',
          payload: toBrandSyncPayload(brand, orgId),
        });
        if (!result.applied) {
          const error = new Error(result.ingestError?.trim() || 'No se pudo crear la marca en el servidor. Reintentá.');
          captureOpsFailure({
            module: 'catalog',
            op: 'catalog.brand_write',
            error,
            ingestError: result.ingestError,
            applied: false,
            path: 'web-ram',
            itemId: id,
            extra: { brandId: id },
          });
          throw error;
        }
        const { appendWebCatalogBrandToCache } = await import('./catalogRamSnapshotCache');
        appendWebCatalogBrandToCache({
          id,
          organization_id: orgId,
          name,
          code: brand.code,
          website: brand.website,
          default_markup_percent: brand.defaultMarkupPercent,
          is_active: brand.isActive,
          deleted_at: null,
          created_at: ts,
          updated_at: ts,
        });
        void logBusinessAuditEvent('catalog.brand.create', {
          brandId: id,
          name,
          code: brand.code,
          organization_id: orgId,
        });
        return id;
};

export const updateBrand = async (params: UpdateBrandParams): Promise<void> => {
  const orgId = await requireSessionOrganizationId();
  const name = normalizeBrandName(params.name);
  if (!name) {
    throw new Error('Ingresá un nombre de marca.');
  }
  const conflicting = await findExistingBrandIdByName(orgId, name, params.id);
  if (conflicting) {
    throw new BrandNameDuplicateError();
  }
  const ts = new Date().toISOString();
  const isActive = params.isActive ?? true;
  const code = params.code?.trim() || null;
  const website = params.website?.trim() || null;
  const defaultMarkupPercent = params.defaultMarkupPercent ?? null;

  const brand: BrandRecord = {
          id: params.id,
          organizationId: orgId,
          name,
          code,
          website,
          defaultMarkupPercent,
          isActive,
          deletedAt: null,
          createdAt: null,
          updatedAt: ts,
        };
        const result = await submitCommand({
          entity: 'brand',
          operation: 'update',
          payload: toBrandSyncPayload(brand, orgId),
        });
        if (!result.applied) {
          const error = new Error(result.ingestError?.trim() || 'No se pudo actualizar la marca en el servidor. Reintentá.');
          captureOpsFailure({
            module: 'catalog',
            op: 'catalog.brand_write',
            error,
            ingestError: result.ingestError,
            applied: false,
            path: 'web-ram',
            itemId: params.id,
            extra: { brandId: params.id },
          });
          throw error;
        }
        const { appendWebCatalogBrandToCache } = await import('./catalogRamSnapshotCache');
        appendWebCatalogBrandToCache({
          id: params.id,
          organization_id: orgId,
          name,
          code,
          website,
          default_markup_percent: defaultMarkupPercent,
          is_active: isActive,
          deleted_at: null,
          updated_at: ts,
        });
        void logBusinessAuditEvent('catalog.brand.update', {
          brandId: params.id,
          name,
          code,
          organization_id: orgId,
        });
        return;
};

export const setBrandActive = async (params: { id: string; isActive: boolean }): Promise<void> => {
  const cached = getCatalogRamSnapshotCache()?.brands.find((b) => String(b.id) === params.id);
        if (!cached) throw new Error('No encontramos la marca.');
        await updateBrand({
          id: params.id,
          name: String(cached.name ?? ''),
          code: cached.code != null ? String(cached.code) : null,
          website: cached.website != null ? String(cached.website) : null,
          defaultMarkupPercent:
            cached.default_markup_percent != null ? Number(cached.default_markup_percent) : null,
          isActive: params.isActive,
        });
        return;
};
